import type { ChecklistItem as FlatChecklistItem } from '../types/schema'
import type { TimelineElement } from '../types/timelineElement'
import type { MeetingRecord } from '../types/meetings'
import type { ExploreWorkspaceSeed } from './workspaceSeeds'
import { PUBLIC_MASK_PERSONAS } from '../data/publicMaskAliases'

export const INTEGRATIONS_DEMO_WORKSPACE_ID = 'explore-seed-integrations-demo'
export const INTEGRATIONS_DEMO_SLUG = 'demo-integrations'
export const INTEGRATIONS_DEMO_NAME = 'Integrations Demo'

const ID = INTEGRATIONS_DEMO_WORKSPACE_ID
const SLUG = INTEGRATIONS_DEMO_SLUG
const BOARD = 'DASHBOARD' as const
const CURRENCY = 'USD'

// Three fake personas from Phase 1 catalog
const OWNER = PUBLIC_MASK_PERSONAS[0] // Aria Patel
const EDITOR = PUBLIC_MASK_PERSONAS[1] // Liam Vance
const VIEWER = PUBLIC_MASK_PERSONAS[2] // Sophia Chen

export const INTEGRATIONS_DEMO_MEMBERS = [
  {
    userId: 'demo-integrations-owner',
    uid: 'demo-integrations-owner',
    displayName: OWNER.name,
    name: OWNER.name,
    email: OWNER.email,
    role: 'ADMIN',
    initials: OWNER.initials,
    colorSeed: OWNER.colorSeed,
  },
  {
    userId: 'demo-integrations-editor',
    uid: 'demo-integrations-editor',
    displayName: EDITOR.name,
    name: EDITOR.name,
    email: EDITOR.email,
    role: 'EDITOR',
    initials: EDITOR.initials,
    colorSeed: EDITOR.colorSeed,
  },
  {
    userId: 'demo-integrations-viewer',
    uid: 'demo-integrations-viewer',
    displayName: VIEWER.name,
    name: VIEWER.name,
    email: VIEWER.email,
    role: 'VIEWER',
    initials: VIEWER.initials,
    colorSeed: VIEWER.colorSeed,
  },
]

export const INTEGRATIONS_DEMO_CONNECTIONS = [
  {
    id: 'conn-demo-gdrive',
    workspaceId: ID,
    provider: 'google_drive',
    displayName: 'Google Drive — Architecture Vault',
    externalAccountId: OWNER.email,
    externalResourceName: 'Platform Specifications & RFCs 2026',
    status: 'ACTIVE',
    createdAt: '2026-02-01T10:00:00.000Z',
    updatedAt: '2026-09-01T10:00:00.000Z',
  },
  {
    id: 'conn-demo-notion',
    workspaceId: ID,
    provider: 'notion',
    displayName: 'Notion — Engineering Wiki',
    externalAccountId: EDITOR.email,
    externalResourceName: 'Core Platform Knowledge Base',
    status: 'ACTIVE',
    createdAt: '2026-02-05T10:00:00.000Z',
    updatedAt: '2026-09-02T10:00:00.000Z',
  },
  {
    id: 'conn-demo-jira',
    workspaceId: ID,
    provider: 'jira',
    displayName: 'Jira — Core Platform Tracker',
    externalAccountId: VIEWER.email,
    externalResourceName: 'PROJ-Core Engine',
    status: 'ACTIVE',
    createdAt: '2026-02-10T10:00:00.000Z',
    updatedAt: '2026-09-05T10:00:00.000Z',
  },
  {
    id: 'conn-demo-gsheets',
    workspaceId: ID,
    provider: 'google_sheets',
    displayName: 'Google Sheets — Operations & Budget',
    externalAccountId: OWNER.email,
    externalResourceName: 'Platform Operations Ledger 2026',
    status: 'ACTIVE',
    createdAt: '2026-02-12T10:00:00.000Z',
    updatedAt: '2026-09-06T10:00:00.000Z',
  },
  {
    id: 'conn-demo-slack',
    workspaceId: ID,
    provider: 'slack',
    displayName: 'Slack — #product-updates',
    externalAccountId: OWNER.email,
    externalResourceName: '#product-updates',
    status: 'ACTIVE',
    createdAt: '2026-02-15T10:00:00.000Z',
    updatedAt: '2026-09-08T10:00:00.000Z',
  },
]

type ElementInput = {
  id: string
  type: TimelineElement['type']
  title: string
  description: string
  parentId: string | null
  routePath: string
  parentRoutePath?: string
  progress: number
  status: TimelineElement['boardStatus']
  priority: number
  startDate: string
  endDate: string
  budgetLimit: number
  estimatedCost: number
  color: `#${string}`
}

const buildElement = (input: ElementInput): TimelineElement => ({
  id: input.id,
  type: input.type,
  title: input.title,
  description: input.description,
  weight: input.type === 'GOAL' ? 10 : input.type === 'TASK' ? 5 : 2,
  progress: input.progress,
  parentId: input.parentId,
  board: BOARD,
  publicId: `${SLUG.toUpperCase()}-${input.routePath.toUpperCase()}`,
  routePath: input.routePath,
  parentRoutePath: input.parentRoutePath ?? '',
  workspaceId: ID,
  startDate: input.startDate,
  endDate: input.endDate,
  boardStatus: input.status,
  workflowStatus: input.status,
  priority: input.priority,
  dependencyNodeIds: [],
  recordVersion: 1,
  statusSyncOrigin: 'SEED',
  statusSyncVersion: 1,
  readinessScore: input.progress,
  readyChecklistCount: Math.round(input.progress / 25),
  totalChecklistCount: 4,
  budgetLimit: input.budgetLimit,
  estimatedCost: input.estimatedCost,
  color: input.color,
  checklist: [],
  readAccess: ['demo-integrations-owner', 'demo-integrations-editor', 'demo-integrations-viewer'],
  writeAccess: ['demo-integrations-owner', 'demo-integrations-editor'],
  directReadAccess: ['demo-integrations-owner', 'demo-integrations-editor', 'demo-integrations-viewer'],
  directWriteAccess: ['demo-integrations-owner', 'demo-integrations-editor'],
  lastUpdatedBy: 'demo-integrations-owner',
})

// Build 10 Goals
const GOAL_SPECS = [
  { id: 'goal-1', title: 'Infrastructure Automation & CI/CD Pipeline', color: '#3b82f6' as const },
  { id: 'goal-2', title: 'Customer Sync & CRM Event Ingestion', color: '#10b981' as const },
  { id: 'goal-3', title: 'Product Analytics & Metric Pipelines', color: '#8b5cf6' as const },
  { id: 'goal-4', title: 'Enterprise Identity & OAuth Delegation', color: '#f59e0b' as const },
  { id: 'goal-5', title: 'Multi-Region Storage Hub Reliability', color: '#06b6d4' as const },
  { id: 'goal-6', title: 'Security & Automated Vulnerability Scanning', color: '#ec4899' as const },
  { id: 'goal-7', title: 'Real-Time Event Webhook Streaming', color: '#6366f1' as const },
  { id: 'goal-8', title: 'Cross-Platform Document Collaboration', color: '#14b8a6' as const },
  { id: 'goal-9', title: 'Billing Gateway & Quota Enforcement', color: '#84cc16' as const },
  { id: 'goal-10', title: 'Incident Response & Operational Runbooks', color: '#e11d48' as const },
]

const TASK_TITLES: Record<string, [string, string, string]> = {
  'goal-1': ['Container Registry Hardening', 'Automated Integration Test Runner', 'Blue-Green Deployment Validation'],
  'goal-2': ['Notion Workspace Contact Ingestion', 'Bi-Directional CRM Status Mirror', 'Webhook Deduplication Queue'],
  'goal-3': ['Real-Time Event Streaming Topic', 'Aggregation Job Scheduler', 'Metric Visualization Dashboard'],
  'goal-4': ['OAuth 2.0 PKCE Flow Handler', 'Token Refresh & Rotation Cron', 'Granular Scope Gate Enforcement'],
  'goal-5': ['Google Drive Watch Channel Renewal', 'Drive Mirror Metadata Cache', 'Chunked Artifact Upload Pipeline'],
  'goal-6': ['Static Dependency Scan Automation', 'Audit Ledger Export Verification', 'Secret Token Leak Preventer'],
  'goal-7': ['Generic Webhook Delivery Dispatcher', 'Exponential Backoff Retry Engine', 'Dead Letter Queue Inspector'],
  'goal-8': ['Notion Page Bidirectional Sync', 'Jira Issue Status Reconciler', 'Document Conflict Resolution Flow'],
  'goal-9': ['Usage Quota Telemetry Sweeper', 'Credit Deduction Atomic Ledger', 'Stripe Billing Webhook Handler'],
  'goal-10': ['Automated Incident Page Trigger', 'Escalation Policy Dispatcher', 'Post-Mortem Runbook Archiver'],
}

const elements: TimelineElement[] = []

GOAL_SPECS.forEach((g, gIdx) => {
  const goalRoute = `g${gIdx + 1}`
  elements.push(
    buildElement({
      id: g.id,
      type: 'GOAL',
      title: g.title,
      description: `Architectural goal coordinating ${g.title.toLowerCase()} across all connected systems.`,
      parentId: null,
      routePath: goalRoute,
      progress: 40 + (gIdx * 5) % 60,
      status: 'IN_PROGRESS',
      priority: 1,
      startDate: '2026-03-01',
      endDate: '2026-10-31',
      budgetLimit: 5000,
      estimatedCost: 3500,
      color: g.color,
    }),
  )

  const tasks = TASK_TITLES[g.id]
  tasks.forEach((tTitle, tIdx) => {
    const taskRoute = `${goalRoute}.t${tIdx + 1}`
    const isDone = (gIdx + tIdx) % 3 === 0
    elements.push(
      buildElement({
        id: `${g.id}-task-${tIdx + 1}`,
        type: 'TASK',
        title: tTitle,
        description: `Operational task: ${tTitle} under ${g.title}.`,
        parentId: g.id,
        routePath: taskRoute,
        parentRoutePath: goalRoute,
        progress: isDone ? 100 : (tIdx + 1) * 30,
        status: isDone ? 'COMPLETE' : 'IN_PROGRESS',
        priority: 2,
        startDate: '2026-03-10',
        endDate: '2026-09-30',
        budgetLimit: 1500,
        estimatedCost: 1000,
        color: g.color,
      }),
    )
  })
})

export const INTEGRATIONS_DEMO_ELEMENTS = elements

// 2 Checklist Books with 10 canvas items total
const checklistItems: FlatChecklistItem[] = [
  // Book 1: Production Deployment Verification
  {
    id: 'chk-prod-1',
    parentContainerId: 'goal-1-task-3',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-DEP-01`,
    routePath: 'g1.t3.c1',
    parentRoutePath: 'g1.t3',
    title: 'Verify Drive watch channels active',
    description: 'Ensure Google Drive webhook watch channels are active and renewing within valid TTL.',
    completionCriteria: 'All channels status == ACTIVE in storage_connections.',
    boardStatus: 'COMPLETE',
    workflowStatus: 'COMPLETED',
    priority: 1,
    startDate: '2026-04-01',
    endDate: '2026-04-05',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-owner',
    assigneeName: OWNER.name,
    lastUpdatedBy: 'demo-integrations-owner',
    lastUpdatedByName: OWNER.name,
    isCompleted: true,
    weightage: 2,
    position: { x: 120, y: 150 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: [],
  },
  {
    id: 'chk-prod-2',
    parentContainerId: 'goal-1-task-3',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-DEP-02`,
    routePath: 'g1.t3.c2',
    parentRoutePath: 'g1.t3',
    title: 'Validate Notion sync webhook response',
    description: 'Trigger test Notion change and assert payload arrives in under 10 seconds.',
    completionCriteria: 'Payload received, status 200 returned.',
    boardStatus: 'COMPLETE',
    workflowStatus: 'COMPLETED',
    priority: 1,
    startDate: '2026-04-06',
    endDate: '2026-04-10',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-editor',
    assigneeName: EDITOR.name,
    lastUpdatedBy: 'demo-integrations-editor',
    lastUpdatedByName: EDITOR.name,
    isCompleted: true,
    weightage: 2,
    position: { x: 320, y: 150 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: ['chk-prod-1'],
  },
  {
    id: 'chk-prod-3',
    parentContainerId: 'goal-1-task-3',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-DEP-03`,
    routePath: 'g1.t3.c3',
    parentRoutePath: 'g1.t3',
    title: 'Jira ticket synchronization test',
    description: 'Verify status update in Timelining propagates to linked Jira ticket without conflict.',
    completionCriteria: 'Jira issue mirrors Timelining status.',
    boardStatus: 'IN_PROGRESS',
    workflowStatus: 'ONGOING',
    priority: 2,
    startDate: '2026-04-11',
    endDate: '2026-04-15',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-editor',
    assigneeName: EDITOR.name,
    lastUpdatedBy: 'demo-integrations-editor',
    lastUpdatedByName: EDITOR.name,
    isCompleted: false,
    weightage: 2,
    position: { x: 520, y: 150 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: ['chk-prod-2'],
  },
  {
    id: 'chk-prod-4',
    parentContainerId: 'goal-1-task-3',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-DEP-04`,
    routePath: 'g1.t3.c4',
    parentRoutePath: 'g1.t3',
    title: 'Audit log emission check',
    description: 'Assert that connection sync events emit corresponding audit ledger entries.',
    completionCriteria: 'Audit rows verified in auditLedger.',
    boardStatus: 'IN_PROGRESS',
    workflowStatus: 'ONGOING',
    priority: 2,
    startDate: '2026-04-16',
    endDate: '2026-04-20',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-viewer',
    assigneeName: VIEWER.name,
    lastUpdatedBy: 'demo-integrations-viewer',
    lastUpdatedByName: VIEWER.name,
    isCompleted: false,
    weightage: 2,
    position: { x: 720, y: 150 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: ['chk-prod-3'],
  },
  {
    id: 'chk-prod-5',
    parentContainerId: 'goal-1-task-3',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-DEP-05`,
    routePath: 'g1.t3.c5',
    parentRoutePath: 'g1.t3',
    title: 'Sign-off on production canary',
    description: 'Confirm canary deployment healthy over 48-hour monitoring window.',
    completionCriteria: 'Error rate < 0.01%.',
    boardStatus: 'TODO',
    workflowStatus: 'CREATED',
    priority: 3,
    startDate: '2026-04-21',
    endDate: '2026-04-25',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-owner',
    assigneeName: OWNER.name,
    lastUpdatedBy: 'demo-integrations-owner',
    lastUpdatedByName: OWNER.name,
    isCompleted: false,
    weightage: 3,
    position: { x: 920, y: 150 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: ['chk-prod-4'],
  },

  // Book 2: OAuth & Webhook Security Audit
  {
    id: 'chk-sec-1',
    parentContainerId: 'goal-4-task-1',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-SEC-01`,
    routePath: 'g4.t1.c1',
    parentRoutePath: 'g4.t1',
    title: 'Audit OAuth token rotation timers',
    description: 'Ensure token expiration windows trigger automatic refresh before cutoff.',
    completionCriteria: 'All expired tokens refreshed with valid tokens.',
    boardStatus: 'COMPLETE',
    workflowStatus: 'COMPLETED',
    priority: 1,
    startDate: '2026-05-01',
    endDate: '2026-05-05',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-owner',
    assigneeName: OWNER.name,
    lastUpdatedBy: 'demo-integrations-owner',
    lastUpdatedByName: OWNER.name,
    isCompleted: true,
    weightage: 2,
    position: { x: 120, y: 350 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: [],
  },
  {
    id: 'chk-sec-2',
    parentContainerId: 'goal-4-task-1',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-SEC-02`,
    routePath: 'g4.t1.c2',
    parentRoutePath: 'g4.t1',
    title: 'Verify webhook signature verification',
    description: 'Confirm inbound webhooks with invalid signatures are rejected with 401.',
    completionCriteria: 'HMAC signature matches secret token.',
    boardStatus: 'COMPLETE',
    workflowStatus: 'COMPLETED',
    priority: 1,
    startDate: '2026-05-06',
    endDate: '2026-05-10',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-editor',
    assigneeName: EDITOR.name,
    lastUpdatedBy: 'demo-integrations-editor',
    lastUpdatedByName: EDITOR.name,
    isCompleted: true,
    weightage: 2,
    position: { x: 320, y: 350 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: ['chk-sec-1'],
  },
  {
    id: 'chk-sec-3',
    parentContainerId: 'goal-4-task-1',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-SEC-03`,
    routePath: 'g4.t1.c3',
    parentRoutePath: 'g4.t1',
    title: 'Masking transform coverage test',
    description: 'Confirm all external emails and resources are obscured for public viewers.',
    completionCriteria: 'No real account email in public responses.',
    boardStatus: 'IN_PROGRESS',
    workflowStatus: 'ONGOING',
    priority: 2,
    startDate: '2026-05-11',
    endDate: '2026-05-15',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-viewer',
    assigneeName: VIEWER.name,
    lastUpdatedBy: 'demo-integrations-viewer',
    lastUpdatedByName: VIEWER.name,
    isCompleted: false,
    weightage: 2,
    position: { x: 520, y: 350 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: ['chk-sec-2'],
  },
  {
    id: 'chk-sec-4',
    parentContainerId: 'goal-4-task-1',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-SEC-04`,
    routePath: 'g4.t1.c4',
    parentRoutePath: 'g4.t1',
    title: 'Validate disconnect revocation flow',
    description: 'Ensure revoking connection removes remote webhooks and clears KMS credentials.',
    completionCriteria: 'Secrets scrubbed, remote watch channel stopped.',
    boardStatus: 'TODO',
    workflowStatus: 'CREATED',
    priority: 2,
    startDate: '2026-05-16',
    endDate: '2026-05-20',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-editor',
    assigneeName: EDITOR.name,
    lastUpdatedBy: 'demo-integrations-editor',
    lastUpdatedByName: EDITOR.name,
    isCompleted: false,
    weightage: 2,
    position: { x: 720, y: 350 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: ['chk-sec-3'],
  },
  {
    id: 'chk-sec-5',
    parentContainerId: 'goal-4-task-1',
    workspaceId: ID,
    workspaceSlug: SLUG,
    board: BOARD,
    publicId: `${SLUG.toUpperCase()}-SEC-05`,
    routePath: 'g4.t1.c5',
    parentRoutePath: 'g4.t1',
    title: 'Compliance and penetration review',
    description: 'Final security approval and review before broad team enablement.',
    completionCriteria: 'Security team green approval stamp.',
    boardStatus: 'TODO',
    workflowStatus: 'CREATED',
    priority: 3,
    startDate: '2026-05-21',
    endDate: '2026-05-25',
    recordVersion: 1,
    statusSyncOrigin: 'SEED',
    statusSyncVersion: 1,
    createdById: 'demo-integrations-owner',
    createdByName: OWNER.name,
    assigneeId: 'demo-integrations-owner',
    assigneeName: OWNER.name,
    lastUpdatedBy: 'demo-integrations-owner',
    lastUpdatedByName: OWNER.name,
    isCompleted: false,
    weightage: 3,
    position: { x: 920, y: 350 },
    workspaceCanvasLayoutVersion: 1,
    blockedByExternal: null,
    prerequisites: ['chk-sec-4'],
  },
]

export const INTEGRATIONS_DEMO_CHECKLIST_ITEMS = checklistItems

// 5 Meetings
export const INTEGRATIONS_DEMO_MEETINGS: MeetingRecord[] = [
  {
    id: 'meet-demo-1',
    workspaceId: ID,
    title: 'Sprint Sync & Integration Architecture',
    description: 'Review cross-provider integration health, active sync jobs, and scope expansions.',
    status: 'SCHEDULED',
    sourceProvider: 'GOOGLE_MEET',
    providerMeetingId: 'meet-sync-101',
    joinUrl: 'https://meet.google.com/abc-demo-sync',
    scheduledAt: new Date('2026-09-15T14:00:00Z'),
    durationMinutes: 45,
    timezone: 'UTC',
    agendaMarkdown: '## Agenda\n1. Review Drive Watch channel renewals\n2. Notion sync conflict rate\n3. Jira ticket bidirectional sync',
    agendaNodes: [
      { nodeId: 'goal-1', nodeType: 'ELEMENT', title: 'Infrastructure Automation & CI/CD Pipeline' },
      { nodeId: 'goal-5', nodeType: 'ELEMENT', title: 'Multi-Region Storage Hub Reliability' },
    ],
    participants: [
      { userId: 'demo-integrations-owner', displayName: OWNER.name, rsvpStatus: 'ACCEPTED', external: false },
      { userId: 'demo-integrations-editor', displayName: EDITOR.name, rsvpStatus: 'ACCEPTED', external: false },
      { userId: 'demo-integrations-viewer', displayName: VIEWER.name, rsvpStatus: 'TENTATIVE', external: false },
    ],
    projectId: null,
    connectionId: 'conn-demo-gdrive',
    connectionErrorCode: null,
    createdBy: 'demo-integrations-owner',
    createdAt: new Date('2026-09-01T00:00:00Z'),
    updatedAt: new Date('2026-09-01T00:00:00Z'),
    lastStatusSyncAt: null,
  },
  {
    id: 'meet-demo-2',
    workspaceId: ID,
    title: 'Bi-Weekly Storage & Drive Mirror Review',
    description: 'Examine artifact synchronization latency, quota usage, and thumbnail caching.',
    status: 'SCHEDULED',
    sourceProvider: 'GOOGLE_MEET',
    providerMeetingId: 'meet-drive-202',
    joinUrl: 'https://meet.google.com/def-demo-drive',
    scheduledAt: new Date('2026-09-18T16:00:00Z'),
    durationMinutes: 30,
    timezone: 'UTC',
    agendaMarkdown: '## Agenda\n1. Storage quota consumption\n2. Chunked upload error telemetry',
    agendaNodes: [{ nodeId: 'goal-5', nodeType: 'ELEMENT', title: 'Multi-Region Storage Hub Reliability' }],
    participants: [
      { userId: 'demo-integrations-owner', displayName: OWNER.name, rsvpStatus: 'ACCEPTED', external: false },
      { userId: 'demo-integrations-editor', displayName: EDITOR.name, rsvpStatus: 'ACCEPTED', external: false },
    ],
    projectId: null,
    connectionId: 'conn-demo-gdrive',
    connectionErrorCode: null,
    createdBy: 'demo-integrations-owner',
    createdAt: new Date('2026-09-01T00:00:00Z'),
    updatedAt: new Date('2026-09-01T00:00:00Z'),
    lastStatusSyncAt: null,
  },
  {
    id: 'meet-demo-3',
    workspaceId: ID,
    title: 'Security Gate & OAuth Scope Audit',
    description: 'Audit third-party provider scopes, least-privilege compliance, and token storage KMS.',
    status: 'COMPLETED',
    sourceProvider: 'GOOGLE_MEET',
    providerMeetingId: 'meet-sec-303',
    joinUrl: 'https://meet.google.com/ghi-demo-sec',
    scheduledAt: new Date('2026-09-10T10:00:00Z'),
    durationMinutes: 60,
    timezone: 'UTC',
    agendaMarkdown: '## Agenda\n1. OAuth 2.0 PKCE flow verification\n2. KMS ciphertext rotation checklist',
    agendaNodes: [{ nodeId: 'goal-6', nodeType: 'ELEMENT', title: 'Security & Automated Vulnerability Scanning' }],
    participants: [
      { userId: 'demo-integrations-owner', displayName: OWNER.name, rsvpStatus: 'ACCEPTED', external: false },
      { userId: 'demo-integrations-editor', displayName: EDITOR.name, rsvpStatus: 'ACCEPTED', external: false },
      { userId: 'demo-integrations-viewer', displayName: VIEWER.name, rsvpStatus: 'ACCEPTED', external: false },
    ],
    projectId: null,
    connectionId: 'conn-demo-jira',
    connectionErrorCode: null,
    createdBy: 'demo-integrations-owner',
    createdAt: new Date('2026-09-01T00:00:00Z'),
    updatedAt: new Date('2026-09-01T00:00:00Z'),
    lastStatusSyncAt: null,
  },
  {
    id: 'meet-demo-4',
    workspaceId: ID,
    title: 'Inbound Webhook Reliability War Room',
    description: 'Address webhook retry backoff, dead letter queue drain, and deduplication latency.',
    status: 'READY',
    sourceProvider: 'GOOGLE_MEET',
    providerMeetingId: 'meet-hook-404',
    joinUrl: 'https://meet.google.com/jkl-demo-hook',
    scheduledAt: new Date('2026-09-22T13:30:00Z'),
    durationMinutes: 45,
    timezone: 'UTC',
    agendaMarkdown: '## Agenda\n1. Inbound webhook rate limiter tuning\n2. Retry backlog monitor',
    agendaNodes: [{ nodeId: 'goal-7', nodeType: 'ELEMENT', title: 'Real-Time Event Webhook Streaming' }],
    participants: [
      { userId: 'demo-integrations-owner', displayName: OWNER.name, rsvpStatus: 'ACCEPTED', external: false },
      { userId: 'demo-integrations-editor', displayName: EDITOR.name, rsvpStatus: 'TENTATIVE', external: false },
    ],
    projectId: null,
    connectionId: 'conn-demo-notion',
    connectionErrorCode: null,
    createdBy: 'demo-integrations-owner',
    createdAt: new Date('2026-09-01T00:00:00Z'),
    updatedAt: new Date('2026-09-01T00:00:00Z'),
    lastStatusSyncAt: null,
  },
  {
    id: 'meet-demo-5',
    workspaceId: ID,
    title: 'Cross-Workspace Public Demo Retrospective',
    description: 'Quarterly review of public workspace demo metrics, masking effectiveness, and viewer conversion.',
    status: 'SCHEDULED',
    sourceProvider: 'GOOGLE_MEET',
    providerMeetingId: 'meet-retro-505',
    joinUrl: 'https://meet.google.com/mno-demo-retro',
    scheduledAt: new Date('2026-09-28T15:00:00Z'),
    durationMinutes: 60,
    timezone: 'UTC',
    agendaMarkdown: '## Agenda\n1. Explore hub traffic\n2. PII masking audit\n3. Next provider roadmap',
    agendaNodes: [{ nodeId: 'goal-2', nodeType: 'ELEMENT', title: 'Customer Sync & CRM Event Ingestion' }],
    participants: [
      { userId: 'demo-integrations-owner', displayName: OWNER.name, rsvpStatus: 'ACCEPTED', external: false },
      { userId: 'demo-integrations-editor', displayName: EDITOR.name, rsvpStatus: 'ACCEPTED', external: false },
      { userId: 'demo-integrations-viewer', displayName: VIEWER.name, rsvpStatus: 'ACCEPTED', external: false },
    ],
    projectId: null,
    connectionId: null,
    connectionErrorCode: null,
    createdBy: 'demo-integrations-owner',
    createdAt: new Date('2026-09-01T00:00:00Z'),
    updatedAt: new Date('2026-09-01T00:00:00Z'),
    lastStatusSyncAt: null,
  },
]

export const INTEGRATIONS_DEMO_SEED: ExploreWorkspaceSeed = {
  id: ID,
  name: INTEGRATIONS_DEMO_NAME,
  slug: SLUG,
  summary: 'Dedicated live integrations sandbox showcasing Google Drive, Notion, and Jira connections with automatic PII masking.',
  tags: ['integrations', 'google-drive', 'notion', 'jira', 'automation', 'sync'],
  metrics: [
    { label: 'Connected Integrations', value: '3 Active' },
    { label: 'Goals', value: '10' },
    { label: 'Tasks', value: '30' },
    { label: 'Checklists', value: '10 Nodes' },
    { label: 'Meetings', value: '5 Scheduled' },
  ],
  currency: CURRENCY,
  budgetLabel: '$50,000 Total Infrastructure Budget',
  quotaLabel: 'Enterprise Connectors Enabled',
  sensitiveDemo: {
    label: 'Masked External Identities',
    items: [
      `${OWNER.name} (${OWNER.email})`,
      `${EDITOR.name} (${EDITOR.email})`,
      `${VIEWER.name} (${VIEWER.email})`,
      'Google Drive Architecture Vault (conn-demo-gdrive)',
      'Notion Engineering Wiki (conn-demo-notion)',
      'Jira Core Platform Tracker (conn-demo-jira)',
    ],
  },
  members: [
    { name: OWNER.name, email: OWNER.email, role: 'ADMIN' },
    { name: EDITOR.name, email: EDITOR.email, role: 'EDITOR' },
    { name: VIEWER.name, email: VIEWER.email, role: 'VIEWER' },
  ],
  elements: INTEGRATIONS_DEMO_ELEMENTS,
  checklistItems: INTEGRATIONS_DEMO_CHECKLIST_ITEMS,
  featured: true,
  heroGradient: 'AURORA',
  verified: true,
}

export interface WorkspaceResetResult {
  scrubbedDebris: {
    syncJobs: number
    inboundEvents: number
    watchChannels: number
    mirrors: number
  }
  canonicalRestored: {
    elements: number
    checklists: number
    meetings: number
    connections: number
    members: number
  }
}

/**
 * Scrubs test debris and restores canonical seed state for the Integrations Demo workspace.
 * Accepts either client Firestore Db or Firebase Admin Firestore Db.
 */
export async function resetIntegrationsDemoWorkspace(db: any): Promise<WorkspaceResetResult> {
  const result: WorkspaceResetResult = {
    scrubbedDebris: { syncJobs: 0, inboundEvents: 0, watchChannels: 0, mirrors: 0 },
    canonicalRestored: { elements: 0, checklists: 0, meetings: 0, connections: 0, members: 0 },
  }

  // 1. Scrub test debris collections
  const debrisCollections = [
    { coll: 'integration_sync_jobs', key: 'syncJobs' as const },
    { coll: 'integration_inbound_events', key: 'inboundEvents' as const },
    { coll: 'storage_watch_channels', key: 'watchChannels' as const },
    { coll: 'drive_mirrors', key: 'mirrors' as const },
  ]

  // Canonical Workspace & Mirror Documents
  const workspaceDoc = {
    id: ID,
    name: INTEGRATIONS_DEMO_NAME,
    slug: SLUG,
    ownerId: 'demo-integrations-owner',
    kind: 'TEAM',
    credits: 1000,
    plan: 'ENTERPRISE_TIER',
    isPersonal: false,
    isPublic: true,
    publicListedInExplore: true,
    maskingConfig: {
      enabled: true,
      aliasCatalog: 'default',
    },
    previewCover: {
      tagline: 'Live Integrations Sandbox with Google Drive, Notion, and Jira',
      category: 'Operations',
      aboutMarkdown: 'This public workspace demonstrates live integrations with Google Drive, Notion, and Jira, protected by real-time PII masking.',
      heroGradient: 'AURORA',
      featured: true,
      verified: true,
      tags: ['integrations', 'sync', 'google-drive', 'notion', 'jira'],
    },
    updatedAt: new Date().toISOString(),
  }

  const publicMirrorDoc = {
    workspaceId: ID,
    workspaceSlug: SLUG,
    name: INTEGRATIONS_DEMO_NAME,
    ownerId: 'demo-integrations-owner',
    isPersonal: false,
    isPublic: true,
    listedInExplore: true,
    maskingConfig: {
      enabled: true,
      aliasCatalog: 'default',
    },
    cover: workspaceDoc.previewCover,
    updatedAt: new Date().toISOString(),
  }

  if (typeof db?.collection === 'function') {
    // Admin SDK or mock DB
    for (const { coll, key } of debrisCollections) {
      try {
        const snap = await db.collection(coll).where('workspaceId', '==', ID).get()
        if (snap && snap.docs) {
          result.scrubbedDebris[key] = snap.docs.length
          for (const doc of snap.docs) {
            if (typeof doc.ref?.delete === 'function') {
              await doc.ref.delete()
            }
          }
        }
      } catch {
        // Debris collection may be empty or missing in test environment
      }
    }

    await db.collection('workspaces').doc(ID).set(workspaceDoc, { merge: true })
    await db.collection('public_workspaces').doc(SLUG).set(publicMirrorDoc, { merge: true })

    // Restore Members
    for (const m of INTEGRATIONS_DEMO_MEMBERS) {
      await db.collection('workspace_members').doc(`${ID}_${m.userId}`).set({
        ...m,
        workspaceId: ID,
        status: 'ACTIVE',
      }, { merge: true })
      result.canonicalRestored.members++
    }

    // Restore Storage Connections
    for (const conn of INTEGRATIONS_DEMO_CONNECTIONS) {
      await db.collection('storage_connections').doc(conn.id).set(conn, { merge: true })
      result.canonicalRestored.connections++
    }
    await db.collection('connection_metadata').doc(`${ID}:::GOOGLE_SHEETS`).set({
      workspaceId: ID,
      providerId: 'GOOGLE_SHEETS',
      status: 'ACTIVE',
      accountEmail: OWNER.email,
      accountName: OWNER.name,
      createdAt: '2026-02-12T10:00:00.000Z',
      updatedAt: '2026-09-06T10:00:00.000Z',
    }, { merge: true })
    await db.collection('connection_metadata').doc(`${ID}:::SLACK`).set({
      workspaceId: ID,
      providerId: 'SLACK',
      status: 'ACTIVE',
      accountEmail: OWNER.email,
      accountName: OWNER.name,
      channel: '#product-updates',
      teamName: 'Acme Corp',
      createdAt: '2026-02-15T10:00:00.000Z',
      updatedAt: '2026-09-08T10:00:00.000Z',
    }, { merge: true })

    // Restore Elements
    for (const el of INTEGRATIONS_DEMO_ELEMENTS) {
      await db.collection('elements').doc(el.id).set(el, { merge: true })
      result.canonicalRestored.elements++
    }

    // Restore Checklists
    for (const chk of INTEGRATIONS_DEMO_CHECKLIST_ITEMS) {
      await db.collection('checklists').doc(chk.id).set(chk, { merge: true })
      result.canonicalRestored.checklists++
    }

    // Restore Meetings
    for (const meet of INTEGRATIONS_DEMO_MEETINGS) {
      await db.collection('meetings').doc(meet.id).set(meet, { merge: true })
      result.canonicalRestored.meetings++
    }
  } else if (db) {
    // Client modular Firestore
    const { collection, doc, getDocs, query, where, deleteDoc, setDoc } = await import('firebase/firestore')
    for (const { coll, key } of debrisCollections) {
      try {
        const snap = await getDocs(query(collection(db, coll), where('workspaceId', '==', ID)))
        result.scrubbedDebris[key] = snap.docs.length
        for (const d of snap.docs) {
          await deleteDoc(d.ref)
        }
      } catch {
        // Debris collection may be empty or missing
      }
    }

    await setDoc(doc(db, 'workspaces', ID), workspaceDoc, { merge: true })
    await setDoc(doc(db, 'public_workspaces', SLUG), publicMirrorDoc, { merge: true })

    for (const m of INTEGRATIONS_DEMO_MEMBERS) {
      await setDoc(doc(db, 'workspace_members', `${ID}_${m.userId}`), {
        ...m,
        workspaceId: ID,
        status: 'ACTIVE',
      }, { merge: true })
      result.canonicalRestored.members++
    }

    for (const conn of INTEGRATIONS_DEMO_CONNECTIONS) {
      await setDoc(doc(db, 'storage_connections', conn.id), conn, { merge: true })
      result.canonicalRestored.connections++
    }
    await setDoc(doc(db, 'connection_metadata', `${ID}:::GOOGLE_SHEETS`), {
      workspaceId: ID,
      providerId: 'GOOGLE_SHEETS',
      status: 'ACTIVE',
      accountEmail: OWNER.email,
      accountName: OWNER.name,
      createdAt: '2026-02-12T10:00:00.000Z',
      updatedAt: '2026-09-06T10:00:00.000Z',
    }, { merge: true })
    await setDoc(doc(db, 'connection_metadata', `${ID}:::SLACK`), {
      workspaceId: ID,
      providerId: 'SLACK',
      status: 'ACTIVE',
      accountEmail: OWNER.email,
      accountName: OWNER.name,
      channel: '#product-updates',
      teamName: 'Acme Corp',
      createdAt: '2026-02-15T10:00:00.000Z',
      updatedAt: '2026-09-08T10:00:00.000Z',
    }, { merge: true })

    for (const el of INTEGRATIONS_DEMO_ELEMENTS) {
      await setDoc(doc(db, 'elements', el.id), el, { merge: true })
      result.canonicalRestored.elements++
    }

    for (const chk of INTEGRATIONS_DEMO_CHECKLIST_ITEMS) {
      await setDoc(doc(db, 'checklists', chk.id), chk, { merge: true })
      result.canonicalRestored.checklists++
    }

    for (const meet of INTEGRATIONS_DEMO_MEETINGS) {
      await setDoc(doc(db, 'meetings', meet.id), meet, { merge: true })
      result.canonicalRestored.meetings++
    }
  }

  return result
}
